/**
 * Chart-Bundle sessionStorage cache — Workstream C1 final touch
 * (Feb 2026).
 *
 * Persists the AES-encrypted chart-bundle payload (per client_id)
 * across browser tab reloads so a hard refresh on a recently-viewed
 * chart renders instantly with zero loader / skeleton paint while
 * the background revalidation runs.  Pairs with the in-memory SWR
 * cache (`chartBundleEtagCacheRef`) inside App.js.
 *
 * Why sessionStorage (not localStorage):
 *   - Scoped to the tab → multi-tenant practices using shared
 *     workstations stay isolated.
 *   - Drops automatically when the tab closes — no stale PHI
 *     sitting on disk long-term.
 *
 * Quota-fail-safe:
 *   sessionStorage is capped at ~5MB per origin.  A heavy biller
 *   toggling between 30-40 patient histories in a single workday
 *   *will* hit that cap.  setCachedBundle() detects the
 *   QuotaExceededError, purges all prior chart cache keys, and
 *   retries the write exactly once.  If the retry still fails we
 *   log and continue — the in-memory cache still works, the next
 *   open just won't survive a hard refresh.
 *
 * PHI safety:
 *   - All payloads are AES-256 encrypted with a key derived from
 *     the user's JWT signature (see utils/storageCrypto.js).
 *   - On logout / token rotation the derived key changes; old
 *     payloads become un-decryptable and getCachedBundle() returns
 *     null (treated as a cache miss).
 *   - clearChartBundleCache() can be called on explicit logout to
 *     remove every chart key proactively.
 */

import { deriveEncryptionKey, encryptData, decryptData } from './storageCrypto';

// v3 (May 2026): bumped from v2 to invalidate cached bundles whose
// notes payload pre-dates the chart-bundle ``_fetch_notes``
// display-name synthesis (template_name/form_type_name/note_type
// fallback chain).  v2 entries would otherwise be replayed
// instantly from sessionStorage on chart open, rendering blank
// Documents-tab cards until the SWR background revalidation
// arrived ("documents-tab names show up eventually" user race
// report).
const KEY_PREFIX = 'chart_bundle_v3_';

/**
 * Cache shape stored under each key (after AES encryption):
 *   {
 *     etag:    string,   // server-provided ETag for revalidation
 *     payload: object,   // the full chart-bundle response body
 *     ts:      number,   // Date.now() when this was written
 *   }
 *
 * The wrapping ciphertext is a single string, so the only thing
 * sessionStorage sees is one base64 blob per client.
 */

const _getToken = () => {
  // Match the lookup the rest of the app uses
  try {
    return (
      (typeof window !== 'undefined' && window.sessionStorage?.getItem?.('token')) ||
      (typeof window !== 'undefined' && window.localStorage?.getItem?.('token')) ||
      null
    );
  } catch {
    return null;
  }
};

const _key = (clientId) => `${KEY_PREFIX}${clientId}`;

/**
 * Read the cached bundle for a client.  Returns null on miss or any
 * decryption / JSON failure (treated as miss).
 */
export const getCachedBundle = (clientId) => {
  if (!clientId) return null;
  try {
    const raw = window.sessionStorage.getItem(_key(clientId));
    if (!raw) return null;
    const decrypted = decryptData(raw, deriveEncryptionKey(_getToken()));
    if (!decrypted || typeof decrypted !== 'object') return null;
    return decrypted; // { etag, payload, ts }
  } catch (err) {
    console.warn('[chartBundleCache] getCachedBundle failed:', err?.message);
    return null;
  }
};

/**
 * Write the cached bundle for a client.  Quota-fail-safe: on
 * QuotaExceededError, purges ALL prior chart cache keys and retries
 * exactly once.  Subsequent failures are logged and swallowed —
 * caching is a perf optimization, never a correctness requirement.
 */
export const setCachedBundle = (clientId, { etag, payload }) => {
  if (!clientId || !payload) return;
  const key = _key(clientId);
  const blob = { etag, payload, ts: Date.now() };

  const writeOnce = () => {
    const ciphertext = encryptData(blob, deriveEncryptionKey(_getToken()));
    if (!ciphertext) {
      // Encryption itself failed (very rare).  Don't crash, don't retry.
      console.warn('[chartBundleCache] encryption returned null, skipping cache');
      return false;
    }
    window.sessionStorage.setItem(key, ciphertext);
    return true;
  };

  try {
    writeOnce();
  } catch (err) {
    // QuotaExceededError can show up as either err.name === 'QuotaExceededError'
    // (modern browsers) or err.code === 22 (legacy WebKit), per MDN.
    const isQuota =
      err?.name === 'QuotaExceededError' ||
      err?.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err?.code === 22 ||
      err?.code === 1014;
    if (!isQuota) {
      console.error('[chartBundleCache] setCachedBundle failed (non-quota):', err);
      return;
    }
    console.warn('[chartBundleCache] sessionStorage full — purging prior chart keys');
    try {
      const toRemove = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach(k => window.sessionStorage.removeItem(k));
    } catch (purgeErr) {
      console.error('[chartBundleCache] purge sweep failed:', purgeErr);
      return;
    }
    // Retry exactly once.  If it still fails we give up silently
    // (the in-memory SWR cache continues to work; only browser
    // refreshes lose the persistent cache).
    try {
      writeOnce();
    } catch (retryErr) {
      console.error(
        '[chartBundleCache] Failed to cache bundle even after clearance pass:',
        retryErr,
      );
    }
  }
};

/**
 * Clear every chart bundle key — used on explicit logout to ensure
 * no PHI lingers in the tab's storage even before the tab itself
 * is closed.  No-op on miss.
 */
export const clearChartBundleCache = () => {
  try {
    const toRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach(k => window.sessionStorage.removeItem(k));
  } catch (err) {
    console.warn('[chartBundleCache] clearChartBundleCache failed:', err);
  }
};

// Exported for tests.
export const _internal = { KEY_PREFIX };
